import { ChangeDetectorRef, Component, HostListener, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { CacheService } from './services/cache.service';
import { UserModel } from './Models/UserModel';
import { StorageService } from './services/storage.service';
import { StorageKey, StorageType } from './storage-key';
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { UserService } from './services/user.service';
import { AuthService } from './services/auth.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit {
  @HostListener('window:popstate', ['$event'])
  onPopState(event: PopStateEvent) {
    location.reload();
  }
  isLoggedIn: boolean = false;
  userDetails: UserModel | null = null;
  isServer: any;
  isBrowser: any;
  cssScript: any[] = [
    'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css',
    'https://cdnjs.cloudflare.com/ajax/libs/libphonenumber-js/1.10.6/libphonenumber-js.min.js'
  ];
  isContentLoaded: boolean = false;
  showForumAnnouncement: boolean = false;
  showForumAnnouncementDetails: boolean = false;
  isHomeRoute: boolean = false;
  isForumsRoute: boolean = false;
  isOnboardingRoute: boolean = false;

  constructor(
    private cacheService: CacheService,
    private storage: StorageService,
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(DOCUMENT) private dom: any,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private userService: UserService,
    private authService: AuthService
  ) {
    this.isServer = isPlatformServer(this.platformId);
    this.isBrowser = isPlatformBrowser(this.platformId);
    // this.router.events.pipe(
    //   filter(event => event instanceof NavigationEnd)
    // ).subscribe(() => {
    //   this.isContentLoaded = !!this.activatedRoute.snapshot.firstChild;
    // });
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.updateRouteFlags(event.urlAfterRedirects);
        this.showForumAnnouncement = this.isHomeRoute;
        this.showForumAnnouncementDetails = false;
        this.isContentLoaded = false; // Hide footer while navigating
      }
      if (event instanceof NavigationEnd) {
        setTimeout(() => {
          this.isContentLoaded = true; // Show footer after route is loaded
        }, 2000); // Small delay to ensure rendering is complete
      }
    });
  }

  ngOnInit() {
    this.updateRouteFlags(this.router.url);
    this.showForumAnnouncement = this.isHomeRoute;

    if (this.storage.getItem(StorageKey.IS_LOGGED_IN, StorageType.LOCAL) == 'true') {
      this.isLoggedIn = true;
      this.refreshLoggedInUser();
    }
    else if (this.storage.getItem(StorageKey.IS_LOGGED_IN, StorageType.LOCAL) == 'false') {
      this.isLoggedIn = false;
      this.cacheService.onUserDetailsChange(null);
    }
    this.cacheService._userDetails.subscribe((res) => {
      if (res != null) {
        this.isLoggedIn = true;
        this.userDetails = res;
      }
    });
    if (this.router.url == '/') {
      this.loadCSS(this.cssScript);
    }
  }

  private refreshLoggedInUser(): void {
    const userId = this.authService.getUserId() || this.getStoredUserId();

    if (!userId) {
      this.userDetails = null;
      this.cacheService.onUserDetailsChange(null);
      return;
    }

    this.userService.getUserFromID(userId).subscribe({
      next: (user) => {
        if (!user) {
          return;
        }

        this.userDetails = user;
        this.storage.setItem(StorageKey.USER, JSON.stringify(user), StorageType.LOCAL);
        this.storage.setItem(StorageKey.IS_LOGGED_IN, 'true', StorageType.LOCAL);
        this.cacheService.onUserDetailsChange(user);

        // if (this.requiresProfileSetup(user) && !this.isOnboardingRoute) {
        //   void this.router.navigateByUrl('/user/profile-setup');
        // }
      },
      error: () => {
        this.userDetails = null;
        this.cacheService.onUserDetailsChange(null);
      },
    });
  }

  private getStoredUserId(): number {
    const rawUser = this.storage.getItem(StorageKey.USER, StorageType.LOCAL);

    if (!rawUser) {
      return 0;
    }

    try {
      const storedUser = JSON.parse(rawUser) as UserModel;
      return Number(storedUser?.id || storedUser?.userdetails?.userId || storedUser?.userDetails?.userId || 0);
    } catch {
      return 0;
    }
  }

  private requiresProfileSetup(user: UserModel | null): boolean {
    const profileSource = user as any;
    const grade = String(
      profileSource?.grade ??
      profileSource?.Grade ??
      profileSource?.userdetails?.grade ??
      profileSource?.userdetails?.Grade ??
      profileSource?.userDetails?.grade ??
      profileSource?.userDetails?.Grade ??
      ''
    ).trim();

    return !grade;
  }

  private updateRouteFlags(url: string): void {
    const normalizedUrl = url || '';
    this.isHomeRoute = normalizedUrl === '/' || normalizedUrl === '';
    this.isForumsRoute =
      normalizedUrl === '/forums' ||
      normalizedUrl === '/forums/' ||
      normalizedUrl.startsWith('/forums/');
    this.isOnboardingRoute =
      normalizedUrl === '/user/profile-setup' ||
      normalizedUrl.startsWith('/user/profile-setup/');
  }

  dismissForumAnnouncement() {
    this.showForumAnnouncement = false;
    this.showForumAnnouncementDetails = false;
  }

  toggleForumAnnouncementDetails() {
    this.showForumAnnouncementDetails = !this.showForumAnnouncementDetails;
  }

  loadCSS(csslist: any) {
    if (isPlatformBrowser(this.platformId)) {
      if (Array.isArray(csslist)) {
        for (let n = 0; n < csslist.length; n++) {
          const file = csslist[n];
          const isCSS = file.endsWith('.css');
          const isJS = file.endsWith('.js');
          if (isCSS) {
            let link = this.dom.createElement('link');
            link.setAttribute('rel', 'stylesheet');
            this.dom.head.appendChild(link);
            link.setAttribute('href', file);
          } else if (isJS) {
            let script = this.dom.createElement('script');
            this.dom.head.appendChild(script);
            script.setAttribute('src', file);
          }
        }
      }
    }
  }
}
